/**
 * Security configuration and utilities
 * Handles production vs development mode, admin debug mode, and security checks
 */

import { getEnvVar, getEnvBool } from './env'

/**
 * Application mode: 'production' (locked) or 'development' (debug)
 * In production mode:
 * - No debug info exposed to users
 * - Minified, optimized bundles
 * - No verbose error messages
 * - Extra security hardening
 */
export type AppMode = 'production' | 'development'

/**
 * Get the current application mode
 * Defaults to production for security
 */
export function getAppMode(): AppMode {
  const mode = getEnvVar('VITE_APP_MODE', 'production')
  return mode === 'development' ? 'development' : 'production'
}

/**
 * Check if we're in production (locked) mode
 */
export function isProductionMode(): boolean {
  return getAppMode() === 'production'
}

/**
 * Check if we're in development (debug) mode
 * Note: This should only enable debug features for authenticated admins
 */
export function isDevelopmentMode(): boolean {
  return getAppMode() === 'development'
}

/**
 * Check if debug mode is enabled for the current user
 * Debug mode should only be accessible to authenticated admins
 * Even in development mode, regular users should not see debug info
 */
export function isDebugModeEnabled(userRole?: string): boolean {
  // Only admins can access debug mode
  if (userRole !== 'admin') {
    return false
  }
  
  // In production mode, debug is disabled even for admins (unless explicitly enabled)
  if (isProductionMode()) {
    // In production, admins can enable debug via localStorage (client-side only)
    // This is a convenience feature and does not weaken server-side security
    return getEnvBool('VITE_ENABLE_ADMIN_DEBUG', false) || 
           (typeof window !== 'undefined' && localStorage.getItem('admin_debug_enabled') === 'true')
  }
  
  // In development mode, admins can use debug features
  return true
}

/**
 * Sanitize error messages for production
 * Prevents leaking sensitive information in error messages
 */
export function sanitizeErrorMessage(error: Error | unknown, isAdmin: boolean = false): string {
  const isDebug = isDebugModeEnabled(isAdmin ? 'admin' : undefined)
  
  if (isDebug) {
    // In debug mode, show full error details to admins
    if (error instanceof Error) {
      return error.message
    }
    return String(error)
  }
  
  // In production mode, show generic error messages
  if (error instanceof Error) {
    // Check for known error types and provide user-friendly messages
    if (error.message.includes('network') || error.message.includes('fetch')) {
      return 'Network error. Please check your connection and try again.'
    }
    if (error.message.includes('auth') || error.message.includes('permission')) {
      return 'Authentication error. Please log in again.'
    }
    if (error.message.includes('database') || error.message.includes('query')) {
      return 'Data error. Please try again or contact support.'
    }
    // Generic error message for production
    return 'An error occurred. Please try again or contact support if the problem persists.'
  }
  
  return 'An unexpected error occurred.'
}

/**
 * Check if verbose logging should be enabled
 */
export function shouldLogVerbose(userRole?: string): boolean {
  return isDebugModeEnabled(userRole)
}

/**
 * Security headers configuration
 * These should match the headers in vercel.json
 */
export const SECURITY_HEADERS = {
  'Strict-Transport-Security': 'max-age=31536000; includeSubDomains; preload',
  'X-Content-Type-Options': 'nosniff',
  'X-Frame-Options': 'DENY',
  'X-XSS-Protection': '1; mode=block',
  'Referrer-Policy': 'strict-origin-when-cross-origin',
  'Permissions-Policy': 'geolocation=(), microphone=(), camera=(), payment=(), usb=(), magnetometer=(), gyroscope=(), accelerometer=()',
} as const

